import { useMemo, useState } from 'react';
import { useWordsByUnitQuery } from '@entities/Word';

/** «Все» = все, КРОМЕ скрытых; «Скрытые» = только скрытые (с возможностью вернуть). */
export type WordsFilter = 'all' | 'hidden';

export interface WordCounts {
  all: number;
  hidden: number;
}

/**
 * Загрузка + фильтрация/поиск слов модуля для ModuleWords (одна разметка на
 * desktop и mobile): счётчики вкладок и поиск в одном месте.
 *
 * Фильтры сведены к двум: «Все» (видимые) и «Скрытые». Бэк отдаёт и те, и другие
 * (флаг is_hidden), делим здесь — скрытые не теряются, их можно восстановить.
 */
export function useModuleWords(moduleId: string) {
  const { data, isLoading } = useWordsByUnitQuery(moduleId);
  const words = useMemo(() => data ?? [], [data]);
  const [filter, setFilter] = useState<WordsFilter>('all');
  const [query, setQuery] = useState('');

  const counts = useMemo<WordCounts>(
    () => ({
      all: words.filter((w) => !w.isHidden).length,
      hidden: words.filter((w) => w.isHidden).length,
    }),
    [words],
  );

  const filtered = useMemo(() => {
    return words
      .filter((w) => (filter === 'hidden' ? w.isHidden : !w.isHidden))
      .filter((w) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return w.word.toLowerCase().includes(q) || w.ru.toLowerCase().includes(q);
      });
  }, [words, filter, query]);

  return { words, isLoading, filter, setFilter, query, setQuery, counts, filtered };
}
